/**
 * AuraTriggeredToast.tsx — R9 #10
 *
 * 3-second auto-dismiss toast shown when a passive/aura card fires mid-gameplay:
 *   - Climate_Adaptation_Strategy halves a toll or event loss
 *   - Industrial_Revolution / Silicon_Valley_Startup makes next build/draw free
 *     (currently only HalveNextTokenLoss sites fire the toast; build-free is
 *     consumed inside BuildMenu and will be wired in a future iteration)
 *
 * Fires via store `showAuraTriggeredToast` at all HalveNextTokenLoss consumed sites.
 * Click-to-dismiss or auto-hides after 3s.
 *
 * VISIBLE WHEN: auraTriggeredToast !== null
 * TEST PATH: hold Climate_Adaptation_Strategy card → land on opponent facility →
 *   toll is paid → toast appears "Toll halved: 20T → 10T" → auto-hides 3s.
 */

import type React from "react";
import { useEffect } from "react";
import { useGameStore } from "../../store/game-store.js";
import styles from "./AuraTriggeredToast.module.css";

const TOAST_DURATION_MS = 3000;

export function AuraTriggeredToast(): React.ReactElement | null {
  const toast = useGameStore((s) => s.auraTriggeredToast);
  const dismiss = useGameStore((s) => s.dismissAuraTriggeredToast);

  useEffect(() => {
    if (toast === null) return;
    const timer = window.setTimeout(() => {
      dismiss();
    }, TOAST_DURATION_MS);
    return () => window.clearTimeout(timer);
  }, [toast, dismiss]);

  if (toast === null) return null;

  return (
    <button
      type="button"
      className={styles.toast}
      onClick={dismiss}
      aria-label="Dismiss aura effect notification"
      data-testid="aura-triggered-toast"
    >
      <div className={styles.title}>◑ PASSIVE ACTIVATED</div>
      <div className={styles.source} data-testid="aura-triggered-toast-source">
        {toast.sourceCardName}
      </div>
      <div className={styles.message} data-testid="aura-triggered-toast-message">
        {toast.message}
      </div>
    </button>
  );
}
